import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, fork } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { SagaIterator } from '@redux-saga/types';
import { toast } from 'react-toastify';
import { AmlStatusEnum, CheckTypeEnum, SideEnum, amlbotAPI } from '@/api';
import type { AmlCheckRequest, AmlCheckResponse } from '@/api';
import i18n from '@/i18n';
import {
  COMMON_CONTAINER_ID,
  CheckItemTypeEnum,
  navigationPages,
} from '@/config';
import { AmlHistoryItemData } from '@/redux/models';
import { parseError } from '@/redux/helpers';
import { amlCheckMappers } from '@/redux/mappers';
import {
  amlCheckActions,
  amlHistoryActions,
  investigationHistoryActions,
  userActions,
} from '@/redux/actions';

function* checkAml(action: PayloadAction<AmlCheckRequest>): SagaIterator {
  const toastId = toast.loading(i18n.t('translation.btn_checking_state'), {
    containerId: COMMON_CONTAINER_ID,
  });
  try {
    const { payload } = action;
    const response: AmlCheckResponse = yield call(amlbotAPI.amlCheck, payload);
    const data = amlCheckMappers.amlCheck(response);
    if (data) {
      yield put(userActions.setBalance(data.balance));
    }
    yield put(amlHistoryActions.fetchAmlHistory({}));

    if (
      data?.items[0].status === AmlStatusEnum.ERROR ||
      data?.items[0].status === AmlStatusEnum.FAILED
    ) {
      throw new Error(i18n.t('translation.aml_check_errored') || '');
    }

    if (data?.items[0]) {
      yield put(push(navigationPages.DETAILS.path, { item: data.items[0] }));
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);

    if (errorCode) {
      yield put(amlCheckActions.setErrorCode(errorCode));
    }

    yield put(amlCheckActions.setError(message));
  } finally {
    yield put(amlCheckActions.setLoading(false));
    toast.dismiss(toastId);
  }
}

function* investigate(action: PayloadAction<AmlCheckRequest>): SagaIterator {
  const toastId = toast.loading(i18n.t('translation.btn_checking_state'), {
    containerId: COMMON_CONTAINER_ID,
  });
  try {
    const payload = {
      address: '',
      side: SideEnum.DEPOSIT,
      type: CheckTypeEnum.INVESTIGATION,
      ...action.payload,
    };

    const response: AmlCheckResponse = yield call(amlbotAPI.amlCheck, payload);
    const data = amlCheckMappers.amlInvestigation(response);
    if (data) {
      yield put(userActions.setBalance(data.balance));
    }
    yield put(investigationHistoryActions.fetchInvestigationHistory({}));

    if (data.items[0]) {
      yield put(
        push(navigationPages.INVESTIGATION_DETAILS.path, {
          extendedItem: data.items[0],
        }),
      );
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);

    if (errorCode) {
      yield put(amlCheckActions.setErrorCode(errorCode));
    }

    yield put(amlCheckActions.setError(message));
  } finally {
    yield put(amlCheckActions.setLoading(false));
    toast.dismiss(toastId);
  }
}

function* checkAgain(action: PayloadAction<AmlHistoryItemData>): SagaIterator {
  const amlCheck = action.payload;

  if (amlCheck) {
    const payload: AmlCheckRequest = {
      asset: amlCheck.network,
      hash: amlCheck.tx || amlCheck.address,
      side: (amlCheck.direction as SideEnum) || '',
      force: true,
      address:
        amlCheck.itemKindShort === CheckItemTypeEnum.TX ? amlCheck.address : '',
    };

    yield fork(checkAml, amlCheckActions.checkAml(payload));
  }
}

export { checkAml, investigate, checkAgain };
